import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { CustomPaging, DataTypeProvider, PagingState, SortingState, SelectionState } from '@devexpress/dx-react-grid'
import { Link, Paper, Typography, useMediaQuery } from '@mui/material'
import {
    Grid,
    Table,
    TableHeaderRow,
    PagingPanel,
    TableSelection,
    TableColumnResizing,
    TableColumnVisibility,
} from '@devexpress/dx-react-grid-material-ui'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useDispatch, useSelector } from 'react-redux'
import { formatDateTime } from 'app/services/utils/components/FormatDateTime/FormatDateTime'
import NoDataCellWithLoading from 'app/services/utils/components/NoDataCellWithLoading'
import { parseActivityReference } from 'app/services/utils/parseActivityReference'
import { useJumboTheme } from '@jumbo/hooks'
import { getTransactionById } from 'app/redux/store/features/transactions'
import { getPaymentById } from 'app/redux/store/features/payments'
import { getMemberById } from 'app/redux/store/features/members'

const CustomerActivityGridData = ({
    onPageChange,
    onColumnSort,
    currentPage,
    setCurrentPage,
    apiCallInProgress,
    setApiCallInProgress,
}) => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const { customeractivities, loading, error } = useSelector((state) => state.customers)
    const dateColumns = ['activityDate']
    const [sorting, setSorting] = useState([{ columnName: 'activityDate', direction: 'asc' }])
    const pageSize = 25
    const { theme } = useJumboTheme()
    const sm = useMediaQuery(theme.breakpoints.down('sm'))
    const mddown = useMediaQuery(theme.breakpoints.down('md'))
    const md = useMediaQuery(theme.breakpoints.between('md', 'xl'))
    const min = sm || mddown || md

    useEffect(() => {
        if (!apiCallInProgress || loading) return
        setApiCallInProgress(false)

        if (error) handleError(error)
    }, [loading])

    function handleError(error) {
        if (error.status === '401') {
            navigate('/profile/signout')
        } else if (error.detail) {
            enqueueSnackbar(error.detail, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        } else {
            enqueueSnackbar(error, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        }
    }

    const onCurrentPageChange = (pageNumber) => {
        onPageChange(pageNumber)
        setCurrentPage(pageNumber)
    }

    const onSortingChange = (sortOrder) => {
        onColumnSort(sortOrder)
        setSorting(sortOrder)
    }

    const onNavigatingToPaymentDetail = useCallback(
        (id) => {
            dispatch(getPaymentById({ id: id }))
            navigate(`/registrations/payments/details/${id}`, { state: { id: id } })
        },
        [dispatch, navigate]
    )

    const onNavigatingToTxnDetail = useCallback(
        (id) => {
            dispatch(getTransactionById({ id: id, onlineTxn: false }))
            navigate(`/registrations/transactions/details/${id}`, {
                state: { id: id, navigateFrom: 'CustomerActivity' },
            })
        },
        [dispatch, navigate]
    )

    const onNavigatingToCustomerDetail = useCallback(
        (id) => {
            navigate(`/registrations/customers/edit/${id}`, { state: { customerId: id } })
        },
        [navigate]
    )

    const onNavigatingToMemberDetail = useCallback(
        (familyId, memberId) => {
            dispatch(getMemberById({ familyId, id: memberId }))
            navigate(`/registrations/families/${familyId}/member/edit/${memberId}`, {
                state: { familyId, memberId },
            })
        },
        [dispatch, navigate]
    )

    const handleReferenceClick = useCallback(
        (parsed) => {
            if (!parsed?.navigable || !parsed.id) return
            if (parsed.kind === 'txn') return onNavigatingToTxnDetail(parsed.id)
            if (parsed.kind === 'pay') return onNavigatingToPaymentDetail(parsed.id)
            if (parsed.kind === 'cust') return onNavigatingToCustomerDetail(parsed.id)
            if (parsed.kind === 'mem' && parsed.familyId) {
                return onNavigatingToMemberDetail(parsed.familyId, parsed.id)
            }
        },
        [
            onNavigatingToTxnDetail,
            onNavigatingToPaymentDetail,
            onNavigatingToCustomerDetail,
            onNavigatingToMemberDetail,
        ]
    )

    const NoDataCellWithLoadingType = (props) => <NoDataCellWithLoading loading={loading} {...props} />
    const DateTypeProvider = (props) => <DataTypeProvider formatterComponent={formatDateTime} {...props} />

    const renderCustomerNameCell = (row) => {
        return row.firstName ? row.firstName + ' ' + row.lastName : ''
    }

    const ReferenceFormatter = ({ value, row }) => {
        const parsed = parseActivityReference(value, {
            message: row?.message,
            familyId: row?.familyId,
        })
        if (!parsed) return <span />

        if (!parsed.navigable) {
            return <Typography variant='body2'>{parsed.display}</Typography>
        }

        return (
            <Link
                variant='text'
                underline='none'
                onClick={() => handleReferenceClick(parsed)}
                sx={{
                    '&:hover': {
                        color: 'action.active',
                        cursor: 'pointer',
                    },
                }}>
                {parsed.display}
            </Link>
        )
    }

    const ReferenceTypeProvider = (props) => <DataTypeProvider formatterComponent={ReferenceFormatter} {...props} />

    const columns = [
        { name: 'activityDate', title: 'Activity Date' },
        { name: 'firstName', title: 'Customer Name', getCellValue: renderCustomerNameCell },
        { name: 'familyName', title: 'Family Name' },
        { name: 'reference', title: 'Reference' },
        { name: 'message', title: 'Message' },
    ]

    const columnWidth = useMemo(
        () => [
            { columnName: 'activityDate', width: min ? '130px' : '16%', minWidth: '130px', wordWrapEnabled: true },
            { columnName: 'firstName', width: min ? '150px' : '18%', minWidth: '150px', wordWrapEnabled: true },
            { columnName: 'familyName', width: min ? '150px' : '18%', minWidth: '150px', wordWrapEnabled: true },
            { columnName: 'reference', width: min ? '150px' : '22%', minWidth: '150px', wordWrapEnabled: true },
            {
                columnName: 'message',
                title: 'Message',
                width: min ? '200px' : '26%',
                minWidth: '200px',
                wordWrapEnabled: true,
            },
        ],
        [min]
    )

    const [columnWidths, setColumnWidths] = useState(columnWidth)

    useEffect(() => {
        setColumnWidths(columnWidth)
    }, [min])

    return (
        <Paper style={{ position: 'relative' }}>
            <Grid rows={customeractivities.data} columns={columns}>
                <DateTypeProvider for={dateColumns} />
                <ReferenceTypeProvider for={['reference']} />
                <SortingState sorting={sorting} onSortingChange={onSortingChange} columnSortingEnabled={true} />
                <PagingState currentPage={currentPage} onCurrentPageChange={onCurrentPageChange} pageSize={pageSize} />
                <CustomPaging totalCount={customeractivities.totalCount} />
                <Table columnExtensions={columnWidths} noDataCellComponent={NoDataCellWithLoadingType} />
                <TableColumnResizing
                    columnWidths={columnWidths}
                    onColumnWidthsChange={setColumnWidths}
                    defaultColumnWidths={columnWidths}
                    resizingMode='nextColumn'
                    columnExtensions={columnWidths}
                />
                <TableColumnVisibility />
                <TableHeaderRow showSortingControls />
                <SelectionState />
                <TableSelection showSelectionColumn={false} />
                <PagingPanel />
            </Grid>
        </Paper>
    )
}

export default CustomerActivityGridData
